import React, { useCallback } from 'react';
import { useTranslation } from 'localization';

export interface FacetBucket {
  key: string;
  doc_count: number;
}

export interface SearchFacets {
  use_case_tags: FacetBucket[];
  doc_type: FacetBucket[];
  item_type: FacetBucket[];
}

export interface ActiveFilters {
  useCaseTags?: string[];
  docType?: string;
  itemType?: string;
}

export interface SearchFacetsMoleculeProps {
  facets: SearchFacets;
  activeFilters: ActiveFilters;
  onFilterChange: (filters: ActiveFilters) => void;
  className?: string;
}

const SearchFacetsMolecule: React.FC<SearchFacetsMoleculeProps> = ({
  facets,
  activeFilters,
  onFilterChange,
  className = '',
}) => {
  const { t } = useTranslation();

  const hasActiveFilters =
    (activeFilters.useCaseTags && activeFilters.useCaseTags.length > 0) ||
    activeFilters.docType ||
    activeFilters.itemType;

  const handleUseCaseToggle = useCallback(
    (tag: string) => {
      const current = activeFilters.useCaseTags || [];
      const next = current.includes(tag)
        ? current.filter((t) => t !== tag)
        : [...current, tag];
      onFilterChange({
        ...activeFilters,
        useCaseTags: next.length > 0 ? next : undefined,
      });
    },
    [activeFilters, onFilterChange],
  );

  const handleDocTypeToggle = useCallback(
    (docType: string) => {
      onFilterChange({
        ...activeFilters,
        docType: activeFilters.docType === docType ? undefined : docType,
      });
    },
    [activeFilters, onFilterChange],
  );

  const handleItemTypeToggle = useCallback(
    (itemType: string) => {
      onFilterChange({
        ...activeFilters,
        itemType: activeFilters.itemType === itemType ? undefined : itemType,
      });
    },
    [activeFilters, onFilterChange],
  );

  const handleClearAll = useCallback(() => {
    onFilterChange({});
  }, [onFilterChange]);

  const formatUseCaseLabel = (tag: string): string => {
    return tag
      .split(/[-_]/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getDocTypeLabel = (key: string): string => {
    const docTypeKey = `marketplace.search.docTypes.${key}` as const;
    return t(docTypeKey);
  };

  const getItemTypeLabel = (key: string): string => {
    const typeKey = `marketplace.common.itemType.${key}` as const;
    return t(typeKey);
  };

  const classes = ['marketplace-search-facets', className]
    .filter(Boolean)
    .join(' ');

  return (
    <aside className={classes}>
      {hasActiveFilters && (
        <button
          type="button"
          className="marketplace-search-facets__clear"
          onClick={handleClearAll}
        >
          {t('marketplace.search.clearFilters')}
        </button>
      )}

      {facets.doc_type.length > 0 && (
        <div className="marketplace-search-facets__group">
          <h4 className="marketplace-search-facets__group-title">
            {t('marketplace.search.facets.docType')}
          </h4>
          <ul className="marketplace-search-facets__list">
            {facets.doc_type.map((bucket) => {
              const isActive = activeFilters.docType === bucket.key;
              return (
                <li key={bucket.key}>
                  <button
                    type="button"
                    className={[
                      'marketplace-search-facets__item',
                      isActive ? 'marketplace-search-facets__item--active' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => handleDocTypeToggle(bucket.key)}
                  >
                    <span className="marketplace-search-facets__item-label">
                      {getDocTypeLabel(bucket.key)}
                    </span>
                    <span className="marketplace-search-facets__item-count">
                      {bucket.doc_count}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {facets.use_case_tags.length > 0 && (
        <div className="marketplace-search-facets__group">
          <h4 className="marketplace-search-facets__group-title">
            {t('marketplace.search.facets.useCaseTags')}
          </h4>
          <ul className="marketplace-search-facets__list">
            {facets.use_case_tags.map((bucket) => {
              const isActive = (activeFilters.useCaseTags || []).includes(
                bucket.key,
              );
              return (
                <li key={bucket.key}>
                  <button
                    type="button"
                    className={[
                      'marketplace-search-facets__item',
                      isActive ? 'marketplace-search-facets__item--active' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => handleUseCaseToggle(bucket.key)}
                  >
                    <span className="marketplace-search-facets__item-label">
                      {formatUseCaseLabel(bucket.key)}
                    </span>
                    <span className="marketplace-search-facets__item-count">
                      {bucket.doc_count}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {facets.item_type.length > 0 && (
        <div className="marketplace-search-facets__group">
          <h4 className="marketplace-search-facets__group-title">
            {t('marketplace.search.facets.itemType')}
          </h4>
          <ul className="marketplace-search-facets__list">
            {facets.item_type.map((bucket) => {
              const isActive = activeFilters.itemType === bucket.key;
              return (
                <li key={bucket.key}>
                  <button
                    type="button"
                    className={[
                      'marketplace-search-facets__item',
                      isActive ? 'marketplace-search-facets__item--active' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => handleItemTypeToggle(bucket.key)}
                  >
                    <span className="marketplace-search-facets__item-label">
                      {getItemTypeLabel(bucket.key)}
                    </span>
                    <span className="marketplace-search-facets__item-count">
                      {bucket.doc_count}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </aside>
  );
};

export default SearchFacetsMolecule;
